import GoogleAnalyticsUtils from 'chairisher/util/googleanalytics';

import { logFacebookEvent } from 'chairisher/analytics/facebook';
import { logRedditEvent } from 'chairisher/analytics/reddit';
import { getEmail } from 'chairisher/context/account';
import {
    getAdwordsEnhancedConversionAccount,
    getAdwordsEnhancedConversionLabelOrder,
    getAdwordsOrderCreatedEventLabel,
} from 'chairisher/context/tracking';
import { AmplitudeEventProperty, logAmplitudeEvent, trackAdwordsConversion } from 'chairisher/util/analytics';
import { trackCheckoutForPRT } from 'chairisher/thirdparty/pinterestretargeting';

/**
 * Tracks a newly-created order
 *
 * @param {OrderModel} order The order to track
 * @param {Array.<ProductModel>} products A list of products purchased via the order
 * @param {Object.<string, OrderItemModel>} productIdToOrderItemMap Mapping of product ID to OrderItemModel
 * @param {boolean=} optContainsVariant True indicates the order contains a variant
 */
export function trackOrderCreated(order, products, productIdToOrderItemMap, optContainsVariant) {
    trackAdwordsConversion(getAdwordsOrderCreatedEventLabel(), order.getChairishEffectiveRevenue());

    // Google Enhanced Conversion
    const adwordsEnhanceConversionAccount = getAdwordsEnhancedConversionAccount();
    const adwordsEnhancedConversionLabelOrder = getAdwordsEnhancedConversionLabelOrder();

    GoogleAnalyticsUtils.setUserData({ email: getEmail() });

    GoogleAnalyticsUtils.logEvent(
        'purchase',
        {
            currency: 'USD',
            items: products.map((product) => ({
                id: String(product.getId()),
                price: product.getPrice(),
                quantity: productIdToOrderItemMap[product.getId()].quantity,
            })),
            transaction_id: `order_${order.getId()}`,
            value: String(order.getChairishFee()),
            new_customer: order.getIsNewCustomer(),
        },
        [`${adwordsEnhanceConversionAccount}/${adwordsEnhancedConversionLabelOrder}`, 'GA4'],
    );

    //
    // Amplitude Tracking
    //

    logAmplitudeEvent('cart - order create', {
        [AmplitudeEventProperty.CONTAINS_VARIANT]: !!optContainsVariant,
        [AmplitudeEventProperty.ORDER_ID]: order.id,
    });

    //
    // Third Party Tracking
    //

    // Bing UET
    window.uetq = window.uetq || [];
    window.uetq.push('event', 'Order', {
        event_category: 'Order',
        event_label: 'Order',
        event_value: order.getChairishEffectiveRevenue(),
        revenue_value: order.getChairishEffectiveRevenue(),
        currency: 'USD',
        // TODO: (CHAIR-18953) add back when appropriate
        // new_customer: order.getIsNewCustomer(),
    });

    trackCheckoutForPRT();

    logFacebookEvent([
        'trackCustom',
        'Order',
        {
            content_ids: Object.keys(productIdToOrderItemMap),
            content_type: 'product',
            value: order.getChairishEffectiveRevenue(),
            currency: 'USD',
        },
    ]);

    logRedditEvent([
        'track',
        'Purchase',
        {
            conversionId: `order_${order.getId()}`,
            currency: 'USD',
            email: getEmail(),
            itemCount: products.reduce(
                (total, product) => total + productIdToOrderItemMap[product.getId()].quantity,
                0,
            ),
            products: products.map((product) => ({
                category: product.getTaxonomy(),
                id: String(product.getId()),
                itemPrice: product.getPrice(),
                name: product.getTitle(),
                quantity: productIdToOrderItemMap[product.getId()].quantity,
            })),
            value: order.getChairishEffectiveRevenue(),
        },
    ]);
}

/**
 * Tracks the display of the cart checkout page
 */
export function trackOrderCreatePageview(products) {
    logAmplitudeEvent('screen - cart', {
        cartProductList: products.map(({ id, price, shippingTypeCode }) => ({
            [AmplitudeEventProperty.PRODUCT_ID]: id,
            [AmplitudeEventProperty.PRODUCT_PRICE]: price,
            [AmplitudeEventProperty.SHIPPING_TYPE_CODE]: shippingTypeCode,
        })),
    });
}
